import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  Heading,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Badge,
  Grid,
  GridItem,
  Icon,
} from '@chakra-ui/react';
import { PhoneIcon } from '@chakra-ui/icons';

const UssdSimulator: React.FC = () => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('+263771234567');
  const [userInput, setUserInput] = useState('');
  const [textChain, setTextChain] = useState('');
  const [screenContent, setScreenContent] = useState('Enter USSD code to start...\n\nService Code: *384*27858#');
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [ussdCode, setUssdCode] = useState('');
  const [hasDialed, setHasDialed] = useState(false);
  const toast = useToast();

  const sendUssd = async () => {
    // If hasn't dialed yet, check for USSD code
    if (!hasDialed) {
      if (!ussdCode || ussdCode !== '*384*27858#') {
        toast({
          title: 'Invalid USSD Code',
          description: 'Please enter *384*27858#',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      setHasDialed(true);
    }

    if (!phoneNumber) {
      toast({
        title: 'Error',
        description: 'Please enter a phone number',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    // Start new session or continue existing
    let currentSessionId = sessionId;
    let currentTextChain = textChain;

    if (!currentSessionId) {
      currentSessionId = 'sim-' + Date.now();
      setSessionId(currentSessionId);
      currentTextChain = '';
      setIsSessionActive(true);
    }

    // Build text chain
    if (userInput) {
      currentTextChain = currentTextChain ? `${currentTextChain}*${userInput}` : userInput;
      setTextChain(currentTextChain);
    }

    setIsLoading(true);
    setScreenContent('Connecting...');

    try {
      const response = await fetch(`${window.location.protocol}//${window.location.hostname}:5001/api/ussd/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: currentSessionId,
          phoneNumber: phoneNumber,
          text: currentTextChain,
          serviceCode: '*384*27858#',
        }),
      });

      const data = await response.json();

      // Display response
      setScreenContent(data.message || 'No response');

      // If session ended, reset
      if (data.type === 'END') {
        setIsSessionActive(false);
        setTimeout(() => {
          setSessionId(null);
          setTextChain('');
        }, 100);
      }

      // Clear input
      setUserInput('');
      setUssdCode('');
    } catch (error) {
      console.error('Error:', error);
      setScreenContent('Connection error. Make sure backend is running on port 5000.');
      toast({
        title: 'Connection Error',
        description: 'Could not connect to USSD service',
        status: 'error',
        duration: 3000,
      });
      clearSession();
    } finally {
      setIsLoading(false);
    }
  };

  const clearSession = () => {
    setSessionId(null);
    setTextChain('');
    setIsSessionActive(false);
    setScreenContent('Enter USSD code to start...\n\nService Code: *384*27858#');
    setUserInput('');
    setUssdCode('');
    setHasDialed(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      sendUssd();
    }
  };

  const handleKeypadPress = (key: string) => {
    if (!hasDialed) {
      setUssdCode(prev => prev + key);
    } else {
      setUserInput(prev => prev + key);
    }
  };

  const handleDial = () => {
    sendUssd();
  };

  const handleBackspace = () => {
    if (!hasDialed) {
      setUssdCode(prev => prev.slice(0, -1));
    } else {
      setUserInput(prev => prev.slice(0, -1));
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading size="xl" mb={2}>
            📱 USSD Simulator
          </Heading>
          <Text color="gray.600">
            Test the offline USSD functionality without a real phone
          </Text>
        </Box>

        <HStack spacing={8} align="start" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
          {/* Phone Simulator */}
          <Box
            flex="1"
            minW={{ base: '100%', md: '380px' }}
            maxW="420px"
            mx="auto"
          >
            <Card
              bg="gray.900"
              borderRadius="40px"
              p={6}
              boxShadow="2xl"
            >
              <CardBody p={0}>
                <Box
                  bg="white"
                  borderRadius="30px"
                  overflow="hidden"
                  h="720px"
                  display="flex"
                  flexDirection="column"
                >
                  {/* Phone Top Bar */}
                  <HStack
                    justify="space-between"
                    px={4}
                    py={2}
                    bg="white"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <HStack spacing={1}>
                      <Box w="4px" h="4px" bg="black" borderRadius="full" />
                      <Box w="4px" h="4px" bg="black" borderRadius="full" />
                      <Box w="4px" h="4px" bg="black" borderRadius="full" />
                      <Box w="4px" h="4px" bg="black" borderRadius="full" />
                      <Box w="4px" h="4px" bg="gray.300" borderRadius="full" />
                    </HStack>
                    <Text fontSize="xs" fontWeight="bold">Africas Talking</Text>
                    <HStack spacing={1}>
                      <Box as="span">📶</Box>
                      <Box as="span">🔋</Box>
                    </HStack>
                  </HStack>

                  {/* Phone Number Input */}
                  <Box px={4} py={2} bg="blue.50" borderBottom="1px solid" borderColor="blue.200">
                    <Text fontSize="xs" color="blue.700" mb={1} fontWeight="bold">📞 Your Phone Number:</Text>
                    <Input
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+263771234567"
                      size="sm"
                      bg="white"
                      fontSize="sm"
                      fontWeight="bold"
                      textAlign="center"
                      isDisabled={isSessionActive}
                    />
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {isSessionActive ? '🔒 Change after session ends' : '✏️ Edit before dialing'}
                    </Text>
                  </Box>

                  {/* USSD Screen */}
                  <Box
                    flex="1"
                    p={4}
                    bg="gray.50"
                    display="flex"
                    flexDirection="column"
                  >
                    {/* Display Input at Top */}
                    <Box
                      bg="gray.700"
                      color="white"
                      p={3}
                      borderRadius="md"
                      textAlign="center"
                      fontSize="lg"
                      fontWeight="bold"
                      mb={3}
                      minH="50px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {!hasDialed ? (ussdCode || '*384*27858#') : `*384*27858#`}
                    </Box>

                    {/* Response/Menu Display */}
                    <Box
                      flex="1"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={4}
                      overflowY="auto"
                      fontFamily="monospace"
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      color="gray.800"
                    >
                      {isLoading ? (
                        <HStack spacing={2} color="blue.500">
                          <Box as="span" animation="spin 1s linear infinite">⟳</Box>
                          <Text>USSD code running...</Text>
                        </HStack>
                      ) : screenContent}
                    </Box>

                    {/* Show current input being typed */}
                    {hasDialed && (
                      <Box mt={2}>
                        {/* Text Input Field (for names, etc.) */}
                        <Input
                          placeholder="Type here for text input (e.g., name)"
                          value={userInput}
                          onChange={(e) => setUserInput(e.target.value)}
                          onKeyPress={handleKeyPress}
                          textAlign="center"
                          fontSize="lg"
                          fontWeight="bold"
                          bg="white"
                          border="2px solid"
                          borderColor="blue.300"
                          _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                        />
                        {userInput && (
                          <Text mt={1} textAlign="center" fontSize="sm" color="blue.600">
                            You entered: {userInput}
                          </Text>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Phone Keypad */}
                  <Box p={4} bg="white">
                    <Grid templateColumns="repeat(3, 1fr)" gap={3} mb={3}>
                      {[
                        { key: '1', sub: '' },
                        { key: '2', sub: 'ABC' },
                        { key: '3', sub: 'DEF' },
                        { key: '4', sub: 'GHI' },
                        { key: '5', sub: 'JKL' },
                        { key: '6', sub: 'MNO' },
                        { key: '7', sub: 'PQRS' },
                        { key: '8', sub: 'TUV' },
                        { key: '9', sub: 'WXYZ' },
                        { key: '*', sub: '+' },
                        { key: '0', sub: '' },
                        { key: '#', sub: '' },
                      ].map(({ key, sub }) => (
                        <Button
                          key={key}
                          h="60px"
                          onClick={() => handleKeypadPress(key)}
                          bg="gray.100"
                          _hover={{ bg: 'gray.200' }}
                          _active={{ bg: 'gray.300' }}
                          borderRadius="md"
                        >
                          <VStack spacing={0}>
                            <Text fontSize="xl" fontWeight="bold">{key}</Text>
                            {sub && <Text fontSize="xs" color="gray.600">{sub}</Text>}
                          </VStack>
                        </Button>
                      ))}
                    </Grid>

                    {/* Call and Clear Buttons */}
                    <HStack spacing={3}>
                      <Button
                        flex="1"
                        h="60px"
                        colorScheme="red"
                        onClick={handleBackspace}
                        borderRadius="md"
                        fontSize="xl"
                      >
                        ⌫
                      </Button>
                      <Button
                        flex="2"
                        h="60px"
                        colorScheme="green"
                        onClick={handleDial}
                        isLoading={isLoading}
                        borderRadius="full"
                        leftIcon={<PhoneIcon />}
                      >
                        Dial
                      </Button>
                      <Button
                        flex="1"
                        h="60px"
                        colorScheme="orange"
                        onClick={clearSession}
                        borderRadius="md"
                      >
                        Clear
                      </Button>
                    </HStack>

                    {/* Session Info */}
                    <Box mt={3} p={2} bg="gray.50" borderRadius="md" fontSize="xs">
                      <HStack justify="space-between">
                        <Text>
                          <strong>Status:</strong>{' '}
                          {sessionId ? (
                            <Badge colorScheme="green" fontSize="xs">Active</Badge>
                          ) : (
                            <Badge colorScheme="gray" fontSize="xs">Idle</Badge>
                          )}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {phoneNumber}
                        </Text>
                      </HStack>
                    </Box>
                  </Box>
                </Box>
              </CardBody>
            </Card>
          </Box>

          {/* Instructions */}
          <Box flex="1" minW={{ base: '100%', md: '300px' }}>
            <Card>
              <CardHeader>
                <Heading size="md">How to Use</Heading>
              </CardHeader>
              <Divider />
              <CardBody>
                <VStack align="start" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      📋 Available Features:
                    </Text>
                    <VStack align="start" spacing={1} pl={4}>
                      <Text fontSize="sm">• View Product Prices</Text>
                      <Text fontSize="sm">• Add Products for Sale</Text>
                      <Text fontSize="sm">• View My Products</Text>
                      <Text fontSize="sm">• Register Account</Text>
                    </VStack>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      🎯 Instructions:
                    </Text>
                    <VStack align="start" spacing={2} pl={4}>
                      <Text fontSize="sm">
                        1. Enter the USSD code: *384*27858#
                      </Text>
                      <Text fontSize="sm">
                        2. Click "Dial" to start the session
                      </Text>
                      <Text fontSize="sm">
                        3. Main menu will appear
                      </Text>
                      <Text fontSize="sm">
                        4. Enter a number (1-4) to select options
                      </Text>
                      <Text fontSize="sm">
                        5. Follow the prompts on screen
                      </Text>
                      <Text fontSize="sm">
                        6. Use "0" to go back or exit
                      </Text>
                    </VStack>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2} color="green.600">
                      💡 Note:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      This is a simulator for demonstration purposes. In production,
                      users would dial a USSD code like *123# from their mobile phones.
                    </Text>
                  </Box>

                  <Divider />

                  <Box w="full" p={3} bg="blue.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={1}>
                      🎓 Perfect for Presentations!
                    </Text>
                    <Text fontSize="xs" color="blue.600">
                      This simulator mimics the Africa's Talking USSD experience.
                      Dial *384*27858# to start, just like on a real feature phone!
                    </Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </Box>
        </HStack>
      </VStack>
    </Container>
  );
};

export default UssdSimulator;
